import { ReactElement, useCallback, useMemo, useState } from 'react'
import { getStiffeningSegmentPlanAxis } from '@editorUtils'
import { Vector3 } from 'three'
import { OpenWith, RestartAlt, TextRotationDown, TextRotationNone } from '@mui/icons-material'
import { useGetPlanLabelPositions } from '@api/plan-label-positions/plan-label-positions'
import {
  getBeamLabelCenter,
  getColumnLabelCenter,
  getPositionLabelPlacementWorld,
  getStoreyTopZLookup,
  getPurlinLabelCenter,
  getSlabLabelCenter,
  getWallLabelPlacement,
} from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/labelPlacements'
import { makePlanSlabLabelLines } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/planLabelText'
import {
  parseStiffeningAnchorLabelGuid,
  StiffeningAnchorLabelKind,
} from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/stiffeningAnchorLabelGuid'
import useFloorplanLabelDescriptors from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/useFloorplanLabelDescriptors'
import useLabelVisibilityFlags from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/useLabelVisibilityFlags'
import usePositionLabelDescriptors from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/usePositionLabelDescriptors'
import useStiffeningAnchorLabelDescriptors from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/useStiffeningAnchorLabelDescriptors'
import { getCenterMultipleReduceMean, ImmutableVector3, useCameraStore } from '@modugen/ui-toolkit'
import { useControlStore, useModelStore, useSceneStore, useStiffeningStore } from '@editorStores'
import {
  useElementIdToAssembly,
  useElementLabel,
  useFloorplanGuidToHidden,
  useGuidToCrossSection,
  useHideLabels,
  usePlanLabelPdfMetrics,
  useSelectedPlanElementId,
  useFloorplanModel,
} from '@editorHooks'
import { calculateStructuralAssemblyThickness } from 'src/components/manager/assemblies/utils'
import { ElementLabelDescriptor } from 'src/components/scene/ModelElementLabels/types'
import BatchedElementLabels from 'src/components/scene/ModelElementLabelsV2/BatchedElementLabels'
import { LABEL_OCCLUSION_DEPTH_STEP } from 'src/components/scene/ModelElementLabelsV2/constants'
import { LabelPlanExtent } from 'src/components/scene/ModelElementLabelsV2/utils'
import { usePersistentParams } from 'src/hooks'
import SceneHud, { SceneHudAction } from '../SceneHud'
import useScenePositionDrag, { ScenePosition } from '../hooks/useScenePositionDrag'
import usePlanLabelPositionMutations from './usePlanLabelPositionMutations'
import usePlanLabelPositionOverrides from './usePlanLabelPositionOverrides'
import {
  applyPlanLabelOverride,
  getLabelOrientation,
  getRotatedLabelCenter,
  toggleLabelOrientation,
} from './utils'

type SelectedLabelElement =
  | { kind: 'wall'; wall: Wall }
  | { kind: 'beam'; element: PlanLineElement }
  | { kind: 'purlin'; element: PlanLineElement }
  | { kind: 'column'; element: PlanLineElement }
  | { kind: 'position'; position: Position }
  | { kind: 'vertical_slabs' | 'vertical_roof_slabs'; slab: ShapeObject }
  | { kind: 'stiffening_segment'; segment: StiffeningSegment }
  | { kind: 'stiffening_anchor'; segment: StiffeningSegment; anchorKind: StiffeningAnchorLabelKind }

/**
 * HUD for a selected floorplan element label (wall / beam / purlin / column /
 * rip / lintel / Deckenposition / Dachposition, and on the stiffening plan
 * preview stiffening segment / Zuganker / Schubanker): move it in the plan or rotate
 * it by 90° (both persisted as the element's plan label position, which the
 * plan PDF exports honour), or reset it back to the automatic placement and
 * orientation. Labels are selected by clicking them in the floorplan
 * (`FloorplanModelLabels`); the moved position applies to every plan type the
 * element appears on.
 */
const PlanLabelController = (): ReactElement | null => {
  const selectedGuid = useSelectedPlanElementId('element-label')
  const floorplanStorey = useControlStore(state => state.floorplanState.storey)
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  // The label position is a plan concept — the HUD only exists in the
  // top-down floorplan, matching where labels are clickable.
  const inFloorplan = floorplanStorey !== null && isOrthographic

  const model = useModelStore(state => state.model)
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const setIsDrawingActive = useControlStore(state => state.setIsDrawingActive)
  const showLabelCrossSections = useControlStore(state => state.showLabelCrossSections)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)

  const metrics = usePlanLabelPdfMetrics()
  const guidToCrossSection = useGuidToCrossSection()
  const elementIdToAssembly = useElementIdToAssembly()
  const getLabel = useElementLabel()
  const { projectId }: { projectId?: string } = usePersistentParams()
  // The model as the floorplan draws it: in the Planansicht, positions are placed on the
  // previewed storey there, so the selected one is looked up here to match its drawn label.
  const visibleModelBase = useFloorplanModel()

  const selectedElement = useMemo<SelectedLabelElement | null>(() => {
    if (!selectedGuid) return null
    const wall = model.walls.find(candidate => candidate.guid === selectedGuid)
    if (wall) return { kind: 'wall', wall }
    const beam = model.beams.find(candidate => candidate.guid === selectedGuid)
    if (beam) return { kind: 'beam', element: beam }
    const purlin = model.purlins.find(candidate => candidate.guid === selectedGuid)
    if (purlin) return { kind: 'purlin', element: purlin }
    const column = model.columns.find(candidate => candidate.guid === selectedGuid)
    if (column) return { kind: 'column', element: column }
    const position = [...visibleModelBase.rips, ...visibleModelBase.lintels].find(
      candidate => candidate.position_guid === selectedGuid,
    )
    if (position) return { kind: 'position', position }
    const slab = model.vertical_slabs.find(candidate => candidate.guid === selectedGuid)
    if (slab) return { kind: 'vertical_slabs', slab }
    const roofSlab = model.vertical_roof_slabs.find(candidate => candidate.guid === selectedGuid)
    if (roofSlab) return { kind: 'vertical_roof_slabs', slab: roofSlab }
    const segment = stiffeningIntervals.find(candidate => candidate.guid === selectedGuid)
    if (segment) return { kind: 'stiffening_segment', segment }
    const anchorLabel = parseStiffeningAnchorLabelGuid(selectedGuid)
    const anchorSegment =
      anchorLabel &&
      stiffeningIntervals.find(candidate => candidate.guid === anchorLabel.segmentGuid)
    if (anchorLabel && anchorSegment) {
      return { kind: 'stiffening_anchor', segment: anchorSegment, anchorKind: anchorLabel.kind }
    }
    return null
  }, [model, visibleModelBase, selectedGuid, stiffeningIntervals])

  // Same storey tops as the descriptors, which lift the slab and purlin labels to them.
  const storeyTopZ = useMemo(() => getStoreyTopZLookup(visibleModelBase), [visibleModelBase])

  // Where the label sits without an override — the same math the descriptors
  // use, so HUD and drag origin match the drawn label exactly. The stiffening
  // plan's labels depend on data only their descriptors load (the anchor
  // labels), so theirs is taken from the descriptor below instead.
  const computedDefaultCenter = useMemo<ImmutableVector3 | null>(() => {
    if (!selectedElement) return null
    switch (selectedElement.kind) {
      case 'wall': {
        const assembly = elementIdToAssembly[selectedElement.wall.guid]
        return (
          getWallLabelPlacement(
            selectedElement.wall,
            assembly && calculateStructuralAssemblyThickness(assembly),
            metrics,
          )?.position ?? null
        )
      }
      case 'beam':
        return getBeamLabelCenter(selectedElement.element)
      case 'purlin':
        return getPurlinLabelCenter(
          selectedElement.element,
          storeyTopZ[selectedElement.element.storey],
        )
      case 'column': {
        const crossSection = guidToCrossSection[selectedGuid ?? '']
        // matches the descriptors: the cross section adds a second label line
        const numLabelLines = showLabelCrossSections && crossSection ? 2 : 1
        return getColumnLabelCenter(selectedElement.element, crossSection, metrics, numLabelLines)
      }
      case 'position': {
        const wall = model.walls.find(
          candidate => candidate.guid === selectedElement.position.wall_guid,
        )
        const [p0, p1] = wall?.shape.points ?? []
        const crossSection = guidToCrossSection[selectedGuid ?? '']
        return (
          getPositionLabelPlacementWorld({
            position: selectedElement.position,
            wallIsYCartesian: p0 && p1 ? p0.x === p1.x : undefined,
            crossSectionHeight: crossSection?.shape?.height,
            metrics,
            numLabelLines: showLabelCrossSections && crossSection ? 2 : 1,
            storeyTopZ: getStoreyTopZLookup(model)[selectedElement.position.storey],
          })?.position ?? null
        )
      }
      case 'vertical_slabs':
      case 'vertical_roof_slabs':
        // matches the descriptors, whose text lines decide the placement
        return getSlabLabelCenter(
          selectedElement.slab,
          makePlanSlabLabelLines(
            getLabel(selectedElement.slab.guid),
            elementIdToAssembly[selectedElement.slab.guid],
            showLabelCrossSections,
          ),
          metrics,
          storeyTopZ[selectedElement.slab.storey],
        )
      case 'stiffening_segment':
      case 'stiffening_anchor':
        return null
    }
  }, [
    selectedElement,
    model,
    guidToCrossSection,
    selectedGuid,
    metrics,
    elementIdToAssembly,
    showLabelCrossSections,
    getLabel,
    storeyTopZ,
  ])

  // The element's own plan center, which tells rotating which side of the
  // element the label sits on.
  const elementCenter = useMemo<{ x: number; y: number } | null>(() => {
    if (!selectedElement) return null
    switch (selectedElement.kind) {
      case 'wall': {
        const [p0, p1] = selectedElement.wall.shape.points
        return p0 && p1 ? p0.add(p1).divideScalar(2) : null
      }
      case 'beam':
      case 'purlin':
        return getBeamLabelCenter(selectedElement.element)
      case 'column':
        // the floorplan draws the footprint around the upper end point
        return selectedElement.element.shape.end
      case 'position': {
        const { start, end } = selectedElement.position
        return start.add(end).divideScalar(2)
      }
      case 'vertical_slabs':
      case 'vertical_roof_slabs':
        return getCenterMultipleReduceMean(selectedElement.slab.shape.points)
      case 'stiffening_segment':
      case 'stiffening_anchor': {
        // A Zuganker label sits on its segment, so it turns in place.
        if (
          selectedElement.kind === 'stiffening_anchor' &&
          selectedElement.anchorKind !== 'shear-anchor'
        ) {
          return null
        }
        const wall = model.walls.find(
          candidate => candidate.guid === selectedElement.segment.element_guid,
        )
        const axis = wall && getStiffeningSegmentPlanAxis(wall, selectedElement.segment)
        return axis ? axis.start.add(axis.end).divideScalar(2) : null
      }
    }
  }, [selectedElement, model.walls])

  // Hide this label from the batched floorplan render for as long as its HUD
  // is open — this component builds and draws its own copy below instead, at
  // the current (possibly in-progress-drag) position.
  useHideLabels(inFloorplan ? selectedGuid : null)

  // Persisted override, with the live drag position layered on top; drives
  // HUD anchor and drag origin.
  const overrides = usePlanLabelPositionOverrides()

  // Persisted overrides only (without the drag preview) gate the reset action.
  const { data: persistedPositions } = useGetPlanLabelPositions(projectId as string)
  const hasPersistedOverride = useMemo(
    () => !!persistedPositions?.some(entry => entry.elementGuid === selectedGuid),
    [persistedPositions, selectedGuid],
  )

  const { setLabelPosition, resetLabelPosition } = usePlanLabelPositionMutations()

  // Moving keeps a rotated label's orientation.
  const commitPosition = useCallback(
    (guid: string, position: ScenePosition) =>
      setLabelPosition(guid, {
        x: position.x,
        y: position.y,
        orientation: overrides[guid]?.orientation,
      }),
    [setLabelPosition, overrides],
  )

  const { startDrag, activeDragMode, dragPreview } = useScenePositionDrag({
    storeyBoundaries,
    setIsDrawingActive,
    onCommit: commitPosition,
  })

  // Independently build the same descriptors `FloorplanModelLabels` builds
  // for its batch (rather than receiving the selected one via a store), so
  // this component owns both the hide (`useHideLabels` above) and the
  // replacement it draws in its place — no cross-component publish/subscribe,
  // so nothing here has to be timed against the batch's own render.
  // `useFloorplanModel` (the visible model as the floorplan draws it) filters out walls/beams/purlins/columns/slabs hidden by a
  // type/storey visibility toggle or an explicit hide — but the selected one
  // should stay visible here regardless, so add it back if that dropped it.
  const visibleModel = useMemo(() => {
    if (!selectedElement || !selectedGuid) return visibleModelBase
    switch (selectedElement.kind) {
      case 'wall':
        if (visibleModelBase.walls.some(candidate => candidate.guid === selectedGuid)) {
          return visibleModelBase
        }
        return { ...visibleModelBase, walls: [...visibleModelBase.walls, selectedElement.wall] }
      case 'beam':
        if (visibleModelBase.beams.some(candidate => candidate.guid === selectedGuid)) {
          return visibleModelBase
        }
        return {
          ...visibleModelBase,
          beams: [...visibleModelBase.beams, selectedElement.element],
        }
      case 'purlin':
        if (visibleModelBase.purlins.some(candidate => candidate.guid === selectedGuid)) {
          return visibleModelBase
        }
        return {
          ...visibleModelBase,
          purlins: [...visibleModelBase.purlins, selectedElement.element],
        }
      case 'column':
        if (visibleModelBase.columns.some(candidate => candidate.guid === selectedGuid)) {
          return visibleModelBase
        }
        return {
          ...visibleModelBase,
          columns: [...visibleModelBase.columns, selectedElement.element],
        }
      case 'position':
        // rips/lintels aren't filtered by `useVisibleModel` in the first
        // place — their visibility is handled entirely via `guidToHidden`.
        return visibleModelBase
      case 'vertical_slabs':
      case 'vertical_roof_slabs': {
        const slabs = visibleModelBase[selectedElement.kind]
        if (slabs.some(candidate => candidate.guid === selectedGuid)) return visibleModelBase
        return { ...visibleModelBase, [selectedElement.kind]: [...slabs, selectedElement.slab] }
      }
      case 'stiffening_segment':
      case 'stiffening_anchor':
        // drawn for the walls' segments, whose visibility is handled via `guidToHidden`
        return visibleModelBase
    }
  }, [visibleModelBase, selectedElement, selectedGuid])
  const guidToColor = useSceneStore(state => state.guidToColor)
  const floorplanGuidToHidden = useFloorplanGuidToHidden()
  // The selected label stays visible even if its element is hidden (a
  // visibility filter, an explicit hide, or the "active element" hide used
  // while editing) — otherwise hiding the element out from under an open HUD
  // would make the label vanish while the HUD stays put.
  const guidToHidden = useMemo(() => {
    if (!selectedGuid || !floorplanGuidToHidden[selectedGuid]) return floorplanGuidToHidden
    return { ...floorplanGuidToHidden, [selectedGuid]: false }
  }, [floorplanGuidToHidden, selectedGuid])
  const {
    showInnerWallLabels,
    showOuterWallLabels,
    showBeamLabels,
    showVerticalSlabLabels,
    showVerticalRoofSlabLabels,
    showPurlinLabels,
    showRipLabels,
    showLintelLabels,
    showColumnLabels,
  } = useLabelVisibilityFlags()

  const positionLabelDescriptors = usePositionLabelDescriptors({
    visibleModel,
    guidToHidden,
    guidToCrossSection,
    showRipLabels,
    showLintelLabels,
    showCrossSections: showLabelCrossSections,
  })
  const floorplanDescriptors = useFloorplanLabelDescriptors({
    visibleModel,
    guidToColor,
    guidToHidden,
    guidToCrossSection,
    showInnerWallLabels,
    showOuterWallLabels,
    showBeamLabels,
    showVerticalSlabLabels,
    showVerticalRoofSlabLabels,
    showPurlinLabels,
    showColumnLabels,
    showCrossSections: showLabelCrossSections,
  })
  const stiffeningAnchorDescriptors = useStiffeningAnchorLabelDescriptors({
    visibleModel,
    guidToHidden,
    showInnerWallLabels,
    showOuterWallLabels,
  })
  const allDescriptors = useMemo(
    () => [...floorplanDescriptors, ...positionLabelDescriptors, ...stiffeningAnchorDescriptors],
    [floorplanDescriptors, positionLabelDescriptors, stiffeningAnchorDescriptors],
  )

  const selectedDescriptor = useMemo(
    () => allDescriptors.find(candidate => candidate.guid === selectedGuid),
    [allDescriptors, selectedGuid],
  )

  const defaultCenter = useMemo<ImmutableVector3 | null>(() => {
    if (
      selectedElement?.kind === 'stiffening_segment' ||
      selectedElement?.kind === 'stiffening_anchor'
    ) {
      return selectedDescriptor?.position ?? null
    }
    return computedDefaultCenter
  }, [selectedElement, selectedDescriptor, computedDefaultCenter])

  const currentCenter = useMemo(() => {
    if (!selectedGuid || !defaultCenter) return null
    const override =
      dragPreview?.id === selectedGuid ? dragPreview.position : overrides[selectedGuid]
    if (!override) return defaultCenter
    return new ImmutableVector3(override.x, override.y, defaultCenter.z)
  }, [selectedGuid, defaultCenter, overrides, dragPreview])

  const persistedOrientation = overrides[selectedGuid ?? '']?.orientation
  const currentOrientation = useMemo(() => {
    if (persistedOrientation) return persistedOrientation
    return selectedDescriptor ? getLabelOrientation(selectedDescriptor) : null
  }, [persistedOrientation, selectedDescriptor])

  // Drawn at `currentCenter` instead of the descriptor's own (default)
  // position, so it tracks the drag, and in the persisted orientation.
  const replacementDescriptors = useMemo<ElementLabelDescriptor[]>(() => {
    if (!currentCenter || !selectedDescriptor) return []
    return [
      applyPlanLabelOverride(
        selectedDescriptor,
        { x: currentCenter.x, y: currentCenter.y, orientation: persistedOrientation },
        // Above the depth slices `FloorplanModelLabels`'s batch assigns its
        // own descriptors, so the replacement is never hidden behind labels
        // it's moved across. This label is always excluded from that batch
        // while selected (`useHideLabels` above), hence the -1.
        currentCenter.z + (allDescriptors.length - 1) * LABEL_OCCLUSION_DEPTH_STEP,
      ),
    ]
  }, [currentCenter, selectedDescriptor, persistedOrientation, allDescriptors.length])

  // The HUD is anchored to this same replacement label's measured footprint
  // (below its bottom edge, centred horizontally), so it clears the label at
  // every zoom level instead of sitting at a fixed screen offset from its
  // center. Falls back to the center until the label has been measured.
  const [labelExtents, setLabelExtents] = useState<Record<string, LabelPlanExtent>>({})
  const extent = labelExtents[selectedGuid ?? '']
  const hudPosition = useMemo(() => {
    if (!currentCenter) return null
    if (!extent) return new Vector3(currentCenter.x, currentCenter.y, currentCenter.z)
    return new Vector3(
      currentCenter.x + (extent.xMin + extent.xMax) / 2,
      currentCenter.y + extent.yMin,
      currentCenter.z,
    )
  }, [currentCenter, extent])

  const hudGroups = useMemo<SceneHudAction[][]>(() => {
    if (!selectedGuid || !currentCenter) return []
    return [
      [
        {
          key: 'move-xy',
          tooltip: 'In der Ebene verschieben',
          icon: <OpenWith fontSize="small" />,
          onPointerDown: event =>
            startDrag(
              'xy',
              selectedGuid,
              { x: currentCenter.x, y: currentCenter.y, z: currentCenter.z },
              event,
            ),
          active: activeDragMode === 'xy',
          dataCy: 'plan-label-move-xy',
        },
        {
          key: 'rotate',
          // Same toggle as the comments' orientation action.
          tooltip:
            currentOrientation === 'Vertical' ? 'Horizontal ausrichten' : 'Vertikal ausrichten',
          icon:
            currentOrientation === 'Vertical' ? (
              <TextRotationNone fontSize="small" />
            ) : (
              <TextRotationDown fontSize="small" />
            ),
          // Keeps the box edge facing the element in place (around the center
          // until the label has been measured); the resulting center is stored
          // along with the orientation (a rotated label is always
          // Center-anchored).
          onClick: () => {
            if (!currentOrientation) return
            const center =
              extent && elementCenter
                ? getRotatedLabelCenter(currentCenter, elementCenter, extent)
                : currentCenter
            setLabelPosition(selectedGuid, {
              x: center.x,
              y: center.y,
              orientation: toggleLabelOrientation(currentOrientation),
            })
          },
          disabled: !currentOrientation || !!activeDragMode,
          dataCy: 'plan-label-rotate',
        },
        {
          key: 'reset',
          tooltip: 'Automatische Position und Ausrichtung wiederherstellen',
          icon: <RestartAlt fontSize="small" />,
          onClick: () => resetLabelPosition(selectedGuid),
          disabled: !hasPersistedOverride,
          dataCy: 'plan-label-reset',
        },
      ],
    ]
  }, [
    selectedGuid,
    currentCenter,
    activeDragMode,
    hasPersistedOverride,
    startDrag,
    currentOrientation,
    extent,
    elementCenter,
    setLabelPosition,
    resetLabelPosition,
  ])

  if (!inFloorplan || !selectedGuid || !hudPosition || hudGroups.length === 0) return null

  return (
    <>
      <SceneHud position={hudPosition} groups={hudGroups} />
      {replacementDescriptors.length > 0 && (
        <BatchedElementLabels
          descriptors={replacementDescriptors}
          occlude
          onLabelExtentsMeasured={setLabelExtents}
        />
      )}
    </>
  )
}

export default PlanLabelController
